import React, { FC, ReactNode } from 'react';
import { toast } from 'react-toastify';
import { Button, Card, CardBody, CardHeader, Row } from 'reactstrap';
import { Order } from '../../shared/orders/model';
import { RegistrationInfo } from '../../shared/registration/model';
import { InfoCard, PageHeader, RichMarkdownIt, UserStateComponent } from '../components';
import { useTranslation } from '../translations';
import {
  Fetcher,
  isLogicError,
  isResourceError,
  useConfig,
  useConvention,
  useFetcher,
  useUserSafe,
} from '../utils';
import { currencyIcon } from '../utils/currencyIcon';
import { LogicError } from '../utils/errorHandling';

export const Dashboard: FC = () => {
  const { ts } = useTranslation();
  const user = useUserSafe();

  const orderFetch = useFetcher(async () => await api.getActiveOrder(user.id), [user.id]);
  const registrationFetch = useFetcher(
    async () => await api.getUserActiveRegistration(user.id),
    [user.id],
  );

  return (
    <UserStateComponent>
      <PageHeader>{ts('common.dashboard')}</PageHeader>
      <Row className="justify-content-center" id="dashboard">
        <InfoCard
          heading="help"
          icon="help"
          status="info"
          text={[ts('you_can_navigate_through_the'), ts('important_notices_and_items_you')]}
        />
        {orderFetch.data ? (
          <OrderStatus order={orderFetch.data} />
        ) : isResourceError(orderFetch.error, 'Order') ? null : (
          <Fetcher inline result={orderFetch} />
        )}
        {registrationFetch.data ? (
          <RegistrationStatus registration={registrationFetch.data} />
        ) : (
          (handleRegistrationError(registrationFetch.error) ?? (
            <Fetcher inline result={registrationFetch} />
          ))
        )}
      </Row>
      <hr />
      {!user.emailVerified && (
        <Card className="mb-10" id="emailNotVerified">
          <CardHeader className="card-color danger" />
          <CardBody className="text-center mt-10">
            <small>
              <RichMarkdownIt source={ts('please_verify_email')} />
            </small>
            <Button
              block
              color="primary"
              onClick={async () => {
                await api.requestVerificationEmail();
                toast.success(ts('a_new_verification_email_has_been_sent'));
              }}
              outline
            >
              {ts('resend_verification_email')}
            </Button>
          </CardBody>
        </Card>
      )}
      <Card>
        <CardHeader className="card-color warning" />
        <CardBody className="text-center mt-10">
          <small>
            <p>{ts('if_your_details_have_changed')}</p>
            <p>{ts('an_incorrect_name_or_address')}</p>
          </small>
        </CardBody>
      </Card>
    </UserStateComponent>
  );
};

const handleRegistrationError = (error?: Error): ReactNode => {
  if (isLogicError(error, LogicError.RegistrationClosed)) {
    // Used for testing, really dude?
    return <div id="registrationClosed" />;
  }

  if (isResourceError(error, 'Registration')) {
    return <CreateRegistration />;
  }

  return undefined;
};

const CreateRegistration: FC = () => {
  const { ts } = useTranslation();
  return (
    <InfoCard
      button={{
        status: 'primary',
        text: 'create_registration',
        to: '/event/register/new',
      }}
      heading="common.register"
      icon="how_to_reg"
      id="createRegistration"
      status="info"
      text={[ts('you_have_not_yet_created_registration.')]}
    />
  );
};

const RegistrationStatus: FC<{ readonly registration: RegistrationInfo }> = ({ registration }) => {
  const { ts } = useTranslation();
  const { maxSubAccounts } = useConvention();
  const text: string[] = [];

  if (registration.paidOrderItem) {
    text.push(ts('registration_is_complete_and_ready'));
  } else {
    text.push(ts('registration_is_not_yet_active'));
  }

  return (
    <InfoCard
      button={{
        status: 'primary',
        outline: true,
        text: maxSubAccounts > 0 ? 'update_registrations' : 'update_registration',
        to: '/event/register',
      }}
      heading={registration.paidOrderItem ? 'registration_complete' : 'registration_pending'}
      icon="how_to_reg"
      id="updateRegistration"
      status={registration.paidOrderItem ? 'info' : 'warning'}
      text={text}
    />
  );
};

const OrderStatus: FC<{ readonly order: Order }> = ({ order }) => {
  const { ts, currency } = useTranslation();
  const { currencySymbol } = useConfig();
  if (order.orderItems.length === 0) {
    return null;
  }

  const total = order.breakdown.total - order.breakdown.paid;

  if (total <= 0) {
    return null;
  }

  return (
    <InfoCard
      button={{
        status: 'danger',
        text: 'pay_now',
        to: '/pay',
        outline: true,
      }}
      heading="outstanding_balance"
      icon={currencyIcon(currencySymbol)}
      id="outstandingBalance"
      status="danger"
      text={[ts('you_currently_have_an_outstanding', currency(total))]}
    />
  );
};
